/**
 * Harvests trend client (hand-rolled).
 *
 * Reads the caller's monthly harvest/sales trend from the NEW endpoint
 * GET /harvests/trend?months=N — a per-month rollup of opened-harvest kg, settled
 * sales, and harvest count, oldest→newest and GAP-FILLED (months with no activity
 * are present with zeroed figures). A parallel backend agent is building this
 * endpoint; orval has NOT been regenerated for it yet, so the DTO + client are
 * hand-rolled on the shared axios mutator (src/api/client.ts → fseAxios), the same
 * pattern as src/api/earnings.ts / buyers.ts. They return the response BODY
 * directly (fseAxios unwraps `response.data`) and raise FseApiError on a non-2xx so
 * TanStack Query routes them to its error path.
 *
 * CANONICAL CONTRACT (single source of truth until orval regen):
 *   GET /harvests/trend?months=6 →
 *     { data: [{ month:'YYYY-MM', totalKg:string, salesAmountMicro:string,
 *                harvestCount:number }] }  (oldest→newest, gap-filled)
 *
 * MONEY: `salesAmountMicro` is micro-ZARP (1e-6 ZARP) carried as a STRING — divide
 * by 1e6 for display (mirrors src/api/marketplace.ts microToZarp). `totalKg` is a
 * PLAIN numeric-as-string (Postgres numeric, precision preserved) — render verbatim
 * / Number() for the chart, never re-multiplied.
 */
import { fseAxios } from './client';

/** A single month's rollup row (oldest→newest on the wire; gap-filled). */
export interface HarvestTrendPoint {
  /** Calendar month, 'YYYY-MM'. */
  month: string;
  /** Sum of opened-harvest total_kg in the month — PLAIN numeric as string. */
  totalKg: string;
  /** Settled sales in the month — micro-ZARP (1e-6 ZARP) as string. */
  salesAmountMicro: string;
  /**
   * Number of harvests opened in the month.
   * @minimum 0
   */
  harvestCount: number;
}

/** GET /harvests/trend response — the standard {data:[]} envelope. */
export interface HarvestTrendList {
  data: HarvestTrendPoint[];
}

/** Query params for the trend rollup (window length in months; optional). */
export interface HarvestTrendParams {
  /** Number of trailing months to return (default server-side; we ask for 6). */
  months?: number;
}

/**
 * Fetch the caller's monthly harvest/sales trend (oldest→newest, gap-filled).
 * Authed (bearer injected by the mutator). Consumers read `.data`.
 */
export function getMyHarvestTrend(
  params: HarvestTrendParams = { months: 6 },
  signal?: AbortSignal,
): Promise<HarvestTrendList> {
  return fseAxios<HarvestTrendList>({
    url: '/harvests/trend',
    method: 'GET',
    params,
    signal,
  });
}
